/** Shared controls only for endpoints that supply their complete authorized dataset. */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { DataTable, parseSorting, type DataTableProps } from "@/shared/components/data-table";
import { TableToolbar } from "@/shared/components/table-toolbar";
import { type TableFacetOption } from "@/shared/components/table-facet";
import { DEFAULT_PAGE_SIZE, PAGE_SIZES, useListState } from "@/shared/lib/list-state";

/** A client-sortable value explicitly supplied by the owning feature. */
export interface CompleteTableSort<T> extends TableFacetOption {
  valueOf: (row: T) => string | number | null;
}

/** Search, sort and page a complete list; never pass a server page or cursor slice. */
export function CompleteDataTable<T>({
  data,
  getSearchText,
  searchDescription,
  sortFields = [],
  ...props
}: Omit<DataTableProps<T>, "sorting" | "onSortingChange" | "pagination"> & {
  getSearchText?: (row: T) => string;
  searchDescription?: string;
  sortFields?: CompleteTableSort<T>[];
}): ReactElement {
  const { t, i18n } = useTranslation();
  const list = useListState(".");
  const query = list.search.q?.toLocaleLowerCase() ?? "";
  const filtered =
    getSearchText && query
      ? data.filter((row) => getSearchText(row).toLocaleLowerCase().includes(query))
      : data;
  const sorts = parseSorting(list.state.sort).flatMap((sort) => {
    const field = sortFields.find((entry) => entry.value === sort.id);
    return field ? [{ ...sort, field }] : [];
  });
  const sorted = sorts.length
    ? [...filtered].sort((first, second) => {
        for (const sort of sorts) {
          const left = sort.field.valueOf(first);
          const right = sort.field.valueOf(second);
          const comparison =
            typeof left === "number" && typeof right === "number"
              ? left - right
              : String(left ?? "").localeCompare(String(right ?? ""), i18n.language, {
                  numeric: true,
                });
          if (comparison) return sort.desc ? -comparison : comparison;
        }
        return 0;
      })
    : filtered;
  const paged =
    data.length > DEFAULT_PAGE_SIZE || list.state.page > 1 || list.search.size !== undefined;
  const page = list.state.page;
  return (
    <>
      <TableToolbar
        filters={[]}
        search={getSearchText ? list.search.q : undefined}
        searchPlaceholder={t("app.search")}
        searchDescription={searchDescription}
        onSearchChange={getSearchText ? (q) => list.update({ q }) : undefined}
        sorting={list.state.sort}
        sortFields={sortFields}
        onSortingChange={list.setSort}
        onReset={() => list.update({ q: undefined })}
      />
      <DataTable
        {...props}
        columns={props.columns.map((column) => {
          const id =
            column.id ?? ("accessorKey" in column ? String(column.accessorKey) : undefined);
          return id && sortFields.some((field) => field.value === id)
            ? { ...column, id, enableSorting: true }
            : column;
        })}
        data={paged ? sorted.slice((page - 1) * list.state.size, page * list.state.size) : sorted}
        sorting={list.state.sort}
        onSortingChange={sortFields.length ? list.setSort : undefined}
        total={sorted.length}
        pagination={
          paged
            ? {
                page,
                pages: Math.max(1, Math.ceil(sorted.length / list.state.size)),
                total: sorted.length,
                pageSize: list.state.size,
                onPageChange: list.setPage,
                onPageSizeChange: list.setSize,
                sizes: PAGE_SIZES,
                extendedNavigation: true,
              }
            : undefined
        }
      />
    </>
  );
}
